import React from 'react';
import { Column, TableInstance, ColumnInstance } from 'react-table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import changeHistoryTableHeader from './changeHistoryTableHeader';

dayjs.extend(utc);

function contains(array: Array<string>, string: string): boolean {
  const index = array.findIndex(item => item === string);
  return index !== -1;
}

export default function useChangeHistoryColumns(
  columnsList?: Array<string>,
): Array<Column<any>> {
  return React.useMemo((): Array<Column<any>> => {
    if (columnsList) {
      const columns: any = [];
      columnsList.forEach(columnName => {
        if (columnName === 'PersonalDetailCategory')
          columns.push(
            getPersonalDetailCategoryHeader(changeHistoryTableHeader),
          );
        if (columnName === 'SectionName')
          columns.push(getSectionNameHeader(changeHistoryTableHeader));
        if (columnName === 'PreviousValue')
          columns.push(getPreviousValueHeader(changeHistoryTableHeader));
        if (columnName === 'CurrentValue')
          columns.push(getCurrentValueHeader(changeHistoryTableHeader));
        if (columnName === 'UpdatedFrom')
          columns.push(getUpdatedFromHeader(changeHistoryTableHeader));
        if (columnName === 'UpdatedBy')
          columns.push(getUpdatedByHeader(changeHistoryTableHeader));
        if (columnName === 'UpdatedAt')
          columns.push(getUpdatedAtHeader(changeHistoryTableHeader));
      });
      return columns;
    }
    return [
      getPersonalDetailCategoryHeader(changeHistoryTableHeader),
      getSectionNameHeader(changeHistoryTableHeader),
      getPreviousValueHeader(changeHistoryTableHeader),
      getCurrentValueHeader(changeHistoryTableHeader),
      getUpdatedFromHeader(changeHistoryTableHeader),
      getUpdatedByHeader(changeHistoryTableHeader),
      getUpdatedAtHeader(changeHistoryTableHeader),
    ];
  }, [columnsList]);
}

function getPersonalDetailCategoryHeader(
  Header: (
    props: TableInstance<any> & {
      column: ColumnInstance<any>;
    },
  ) => JSX.Element,
): Column<any> {
  return {
    Header: Header,
    accessor: (row: any) => `${row.PersonDetailCategory || ''}`,
    id: 'Category',
  };
}

function getSectionNameHeader(
  Header: (
    props: TableInstance<any> & {
      column: ColumnInstance<any>;
    },
  ) => JSX.Element,
): Column<any> {
  return {
    Header: Header,
    accessor: (row: any) => `${row.ColumnName || ''}`,
    id: 'Field name',
  };
}

function getPreviousValueHeader(
  Header: (
    props: TableInstance<any> & {
      column: ColumnInstance<any>;
    },
  ) => JSX.Element,
): Column<any> {
  return {
    Header: Header,
    accessor: (row: any) => row.PreviousValue ?? '',
    id: 'Previous value',
    Cell: ({ value }: { value: string }) => (
      <div style={{ whiteSpace: 'pre-line' }}>{value}</div>
    ),
  };
}

function getCurrentValueHeader(
  Header: (
    props: TableInstance<any> & {
      column: ColumnInstance<any>;
    },
  ) => JSX.Element,
): Column<any> {
  return {
    Header: Header,
    accessor: (row: any) => row.NewValue ?? '',
    id: 'Current value',
    Cell: ({ value }: { value: string }) => (
      <div style={{ whiteSpace: 'pre-line' }}>{value}</div>
    ),
  };
}

function getUpdatedAtHeader(
  Header: (
    props: TableInstance<any> & {
      column: ColumnInstance<any>;
    },
  ) => JSX.Element,
): Column<any> {
  return {
    Header: Header,
    accessor: (row: any) =>
      `${
        row.UpdatedAt !== null
          ? dayjs(row.UpdatedAt).format('MM/DD/YYYY H:mm:ss')
          : ''
      }`,
    id: 'Updated at',
  };
}

function getUpdatedByHeader(
  Header: (
    props: TableInstance<any> & {
      column: ColumnInstance<any>;
    },
  ) => JSX.Element,
): Column<any> {
  return {
    Header: Header,
    accessor: (row: any) => `${row.UpdatedBy || ''}`,
    id: 'Updated by',
  };
}

function getUpdatedFromHeader(
  Header: (
    props: TableInstance<any> & {
      column: ColumnInstance<any>;
    },
  ) => JSX.Element,
): Column<any> {
  return {
    Header: Header,
    accessor: (row: any) => `${row.UpdatedFrom || ''}`,
    id: 'Updated from',
  };
}
